import { useEffect, useRef, useState, type FormEvent } from 'react'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { Bot, Loader2, MessageCircle, Send, TriangleAlert, X } from 'lucide-react'
import { useLanguage } from '@/components/LanguageContext'

interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
}

// Espelha MAX_MESSAGE_LENGTH em supabase/functions/bioinsumos-chat/index.ts —
// antes o Textarea não tinha `maxLength` nenhum e a Edge Function truncava em
// silêncio a 4000 caracteres (`.slice(0, MAX_MESSAGE_LENGTH)`), sem feedback
// ao usuário de que parte da mensagem nunca chegou ao modelo.
const MAX_MESSAGE_LENGTH = 4000

interface ChatSources {
  products: { produto_id: string; produto: string; empresa: string }[]
  companies: { empresa: string; categoria: string }[]
}

/** Evento global disparado por qualquer CTA da landing ("Falar com o Assistente"
 *  em AssistantSection.tsx) para abrir este widget flutuante, que é montado uma
 *  única vez em PublicLayout.tsx e não tem estado exposto a componentes irmãos. */
export const OPEN_CHAT_WIDGET_EVENT = 'agrobio:open-chat-widget'

/**
 * Botão flutuante + painel de conversa do agente interativo de bioinsumos.
 * Consome a Edge Function `bioinsumos-chat` (grounding em bioinputs_catalog +
 * bioinputs_companies e resposta via API da Anthropic). Resposta única (sem
 * streaming) — suficiente para o MVP.
 */
export function ChatWidget() {
  const { t, language } = useLanguage()
  const WELCOME_MESSAGE: ChatMessage = { role: 'assistant', content: t('chat.welcome') }
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const handleOpen = () => setOpen(true)
    window.addEventListener(OPEN_CHAT_WIDGET_EVENT, handleOpen)
    return () => window.removeEventListener(OPEN_CHAT_WIDGET_EVENT, handleOpen)
  }, [])
  const [messages, setMessages] = useState<ChatMessage[]>([WELCOME_MESSAGE])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lastSources, setLastSources] = useState<ChatSources | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const panelTitleId = 'agrobio-chat-title'

  useEffect(() => {
    if (!open) return
    const node = scrollRef.current
    if (node) {
      node.scrollTop = node.scrollHeight
    }
  }, [messages, open, loading])

  // Fix 2026-08-17b: WELCOME_MESSAGE era lido de t('chat.welcome') só no
  // inicializador do useState -- trocar o idioma no LanguageSelector não
  // atualizava a mensagem de boas-vindas já renderizada (primeira bolha
  // ficava em PT mesmo com a interface em EN/ES). Reescreve a boas-vindas
  // quando o idioma muda, mas só enquanto a conversa ainda é só ela (nunca
  // sobrescreve histórico real do usuário).
  useEffect(() => {
    setMessages((prev) =>
      prev.length === 1 && prev[0].role === 'assistant' ? [WELCOME_MESSAGE] : prev
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t])

  // Foco inicial no input ao abrir -- painel não tinha role="dialog" nem
  // foco inicial, então usuário de teclado/leitor de tela abria o chat sem
  // nenhuma pista de onde o foco estava.
  useEffect(() => {
    if (open) {
      inputRef.current?.focus()
    }
  }, [open])

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    const text = input.trim().slice(0, MAX_MESSAGE_LENGTH)
    if (!text || loading) return

    const nextMessages: ChatMessage[] = [...messages, { role: 'user', content: text }]
    setMessages(nextMessages)
    setInput('')
    setError(null)
    setLoading(true)

    try {
      // Envia só role+content (histórico completo) para o modelo ter contexto da conversa.
      // `language` é o idioma atual da UI (Tarefa 12 do PLANO-I18N): o
      // assistente responde nesse idioma, mas o grounding (bula MAPA e
      // legislação brasileira) continua sendo citado no português original,
      // com a fonte. A Edge Function valida o valor (pt|en|es|ja) e cai em
      // 'pt' se vier ausente ou inválido -- ver ./language.ts na função.
      const { data, error: fnError } = await supabase.functions.invoke('bioinsumos-chat', {
        body: {
          messages: nextMessages.map(({ role, content }) => ({ role, content })),
          language,
        },
      })

      if (fnError) throw fnError
      if (!data?.reply) throw new Error('Resposta vazia do agente')

      setMessages((prev) => [...prev, { role: 'assistant', content: data.reply as string }])
      setLastSources((data.sources as ChatSources) ?? null)
    } catch (err) {
      console.error('[AgroBio] chat error:', err)
      // Fix 2026-08-17b: todo erro (incluindo 429 de rate limit -- ver
      // RATE_LIMIT_MAX_REQUESTS na Edge Function) caía na mesma mensagem
      // genérica "tente novamente em instantes", convidando o usuário a
      // repetir imediatamente uma requisição que vai falhar de novo pela
      // próxima hora. O status HTTP está disponível em
      // `fnError.context.status` (FunctionsHttpError do supabase-js) -- só
      // não vem tipado, por isso o acesso defensivo abaixo.
      const status = (err as { context?: { status?: number } } | null)?.context?.status
      setError(status === 429 ? t('chat.rateLimited') : t('chat.error'))
    } finally {
      setLoading(false)
    }
  }

  const remainingChars = MAX_MESSAGE_LENGTH - input.length
  const showCharWarning = remainingChars <= 200

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col items-end gap-3">
      {open && (
        <div
          className="flex h-[min(32rem,70vh)] w-[min(24rem,90vw)] flex-col overflow-hidden rounded-xl border border-border bg-card shadow-xl"
          role="dialog"
          aria-modal="true"
          aria-labelledby={panelTitleId}
        >
          {/* Header */}
          <div className="flex items-center justify-between gap-2 border-b border-border bg-primary px-4 py-3 text-primary-foreground">
            <div className="flex items-center gap-2">
              <Bot className="h-5 w-5" />
              <div>
                <p id={panelTitleId} className="text-sm font-semibold leading-tight">{t('chat.title')}</p>
                <p className="text-xs opacity-80 leading-tight">{t('chat.subtitle')}</p>
              </div>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground"
              onClick={() => setOpen(false)}
              aria-label={t('chat.close')}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Disclaimer */}
          <div className="flex items-start gap-2 border-b border-border bg-muted/50 px-3 py-2 text-[11px] text-muted-foreground">
            <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <p>{t('chat.disclaimer')}</p>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-3 py-3"
            aria-live="polite"
            aria-relevant="additions"
          >
            <div className="flex flex-col gap-3">
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={cn('flex', m.role === 'user' ? 'justify-end' : 'justify-start')}
                >
                  <div
                    className={cn(
                      'max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap',
                      m.role === 'user'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-foreground'
                    )}
                  >
                    {m.content}
                  </div>
                </div>
              ))}

              {loading && (
                <div className="flex justify-start">
                  <div className="flex items-center gap-2 rounded-lg bg-muted px-3 py-2 text-sm text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    {t('chat.loading')}
                  </div>
                </div>
              )}

              {!loading && lastSources && lastSources.products.length > 0 && (
                <div className="rounded-lg border border-border bg-background px-3 py-2 text-[11px] text-muted-foreground">
                  <p className="mb-1 font-medium text-foreground">{t('chat.sourcesTitle')}</p>
                  <ul className="list-disc space-y-0.5 pl-4">
                    {lastSources.products.map((p) => (
                      <li key={p.produto_id}>
                        {p.produto} ({p.empresa})
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {error && (
                <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {error}
                </div>
              )}
            </div>
          </div>

          {/* Input */}
          <form onSubmit={handleSubmit} className="border-t border-border p-3">
            <div className="flex items-end gap-2">
              <Textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    handleSubmit(e as unknown as FormEvent)
                  }
                }}
                placeholder={t('chat.inputPlaceholder')}
                rows={1}
                maxLength={MAX_MESSAGE_LENGTH}
                className="min-h-[40px] max-h-24 resize-none text-sm"
                disabled={loading}
              />
              <Button type="submit" size="icon" disabled={loading || !input.trim()} aria-label={t('chat.send')}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
            {showCharWarning && (
              <p className="mt-1 text-right text-[11px] text-muted-foreground" aria-live="polite">
                {t('chat.charsRemaining', { n: Math.max(remainingChars, 0) })}
              </p>
            )}
          </form>
        </div>
      )}

      <Button
        type="button"
        size="icon"
        className="h-14 w-14 rounded-full shadow-lg"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? t('chat.close') : t('chat.open')}
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </Button>
    </div>
  )
}
